import { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { isEmpty, omit, size } from "lodash"
import { useAccountAlias } from "resources/account"
import NewTrade from "modules/rebalance/components/new-trade"
import { buildContributorKey } from "helpers/contributors"
import { LOADING } from "helpers/data-statuses"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "connected-components/dashboard/analytics"
import { getContributorDetails, getTradesContributors } from "../helpers/renderers"

function NewTradeComponent({ data, modelId, base, addTrade, tradedPosition, setTradedPosition, tradedPositionField, hideEconomicExposure, filter, isActiveTradingDisabled, tradeDisabledReason, trades, currentGrouping = null, hasFilters = false }) {
  const account = useAccountAlias()
  const analytics = useAnalytics()

  const handleSimulateAction = useCallback(
    (security, field, value, isNewPosition) => {
      const trade = { security, field, value, isNewPosition, filter, base }
      analytics.track(DASHBOARD_EVENTS.TRADE_ADDED, {
        securityId: security?.id,
        securityName: security?.name,
        field,
        value,
        isNewPosition,
        hasFilter: !!filter,
        hasBase: !!base,
      })
      addTrade(trade)
    },
    [addTrade, filter, base, analytics],
  )

  // [TODO-JG] This is a hack to get the contributors in the right format for the existing trade component.
  // Will need to refactor it to use the new contract
  const hasTrades = Boolean(size(trades))
  const tradesContributors = useMemo(
    () => getTradesContributors(data, hasTrades),
    [data, hasTrades],
  )

  const workingEquity = useMemo(() => {
    const marketValue = data?.values.marketValue
    if (!marketValue) {
      return null
    }
    return marketValue.referenceEquity || marketValue.gmv
  }, [data])

  const selectedPosition = useMemo(() => {
    if (!tradesContributors || !tradedPosition) {
      return null
    }

    const contributor = tradesContributors.find(c => buildContributorKey(c) === buildContributorKey(tradedPosition))
    const isInPositionSet = Boolean(contributor)
    const targetPosition = getContributorDetails(contributor, hasTrades)

    let position
    if (!contributor) {
      position = {
        ...tradedPosition,
        ...targetPosition,
        isInPositionSet,
      }
    } else {
      position = {
        ...omit(tradedPosition, ["activePercentEquity", "positionPercentEquity", "positionSetEconomicExposure", "economicExposure"]),
        ...omit(getContributorDetails(contributor, false), ["activePercentEquity", "positionPercentEquity", "positionSetEconomicExposure", "economicExposure"]),
        ...getContributorDetails(contributor, hasTrades),
        isInPositionSet,
      }
    }

    return {
      ...position,
      name: position.description,
    }
  }, [tradesContributors, tradedPosition, hasTrades])

  const externalState = useMemo(() => {
    if (!selectedPosition || !tradedPositionField) {
      return null
    }
    return {
      selectedPosition,
      tradedPositionField,
    }
  }, [selectedPosition, tradedPositionField])

  const onReset = useCallback(() => {
    setTradedPosition(null)
  }, [setTradedPosition])

  // If we have both grouped and filteres positions, we want to still display the grouped message
  const tradeMessage = useMemo(() => {
    if (currentGrouping) {
      return `Positions are currently grouped by ${currentGrouping.label}. Traded securities will appear in their corresponding group, and in the ungrouped portfolio.`
    } else if (hasFilters) {
      return "Positions are currently filtered. Traded securities will be displayed based on the applied filters."
    }
    return null
  }, [currentGrouping, hasFilters])

  return (
    <NewTrade
      {...{
        account,
        contributors: tradesContributors,
        currentGrouping,
        isActive: !isEmpty(base),
        modelId,
        newSimulateAction: handleSimulateAction,
        workingEquity,
        tradeDisabledReason,
        tradedPositionField,
        externalState,
        onReset,
        hideEconomicExposure,
        isActiveTradingDisabled,
        tradeMessage,
      }}
    />
  )
}

NewTradeComponent.propTypes = {
  data: PropTypes.object.isRequired,
  modelId: PropTypes.string.isRequired,
  addTrade: PropTypes.func.isRequired,
  base: PropTypes.object,
  tradeDisabledReason: PropTypes.string,
  tradedPosition: PropTypes.object,
  setTradedPosition: PropTypes.func.isRequired,
  tradedPositionField: PropTypes.string,
  hideEconomicExposure: PropTypes.bool,
  filter: PropTypes.object,
  isActiveTradingDisabled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  trades: PropTypes.array.isRequired,
  currentGrouping: PropTypes.object,
  hasFilters: PropTypes.bool,
}

export default function NewTradeRenderer({ status, isExpanded, ...props }) {
  let tradeDisabledReason = props.tradeDisabledReason
  // We leverage the tradeDisabledReason here to avoid having the button disappear while data is loading
  if (status === LOADING) {
    tradeDisabledReason = "Loading..."
  }

  if (isExpanded) {
    tradeDisabledReason = "All trading is disabled when expanded."
  }

  if (status === "error" || !props.data) {
    tradeDisabledReason = "No data"
  }

  return <NewTradeComponent tradeDisabledReason={tradeDisabledReason} {...props} />
}

NewTradeRenderer.propTypes = {
  data: PropTypes.object.isRequired,
  status: PropTypes.oneOf(["loading", "error", "success"]).isRequired,
  isExpanded: PropTypes.bool,
  tradeDisabledReason: PropTypes.string,
}
